// Layout.tsx
import {useCallback, useEffect, useState, useMemo} from "react";
import { Outlet } from "react-router-dom";
import {
    Box,
    CssBaseline,
    Toolbar,
    IconButton,
    Container,
    styled,
} from "@mui/material";
import MuiDrawer from '@mui/material/Drawer';
import { useSettings } from "./contexts/SettingsContext";
import { useAuth } from "./security/AuthProvider";
import Navigation from "./Navigation";
import { Typography } from "@mui/material";
import Spinner from "./ui/Spinner";
import AppBar from "./ui/AppBar";
import {
    IconMainDrawerOpenDrawer,
    IconMainDrawerCloseDrawer,
} from "./components/Icons";
import ReauthModal from "./components/ReauthModal";

interface LayoutProps {}

interface DrawerHeaderProps {
    open: boolean;
    toggleDrawer: () => void;
}

const DrawerHeader = ({ open, toggleDrawer }: DrawerHeaderProps) => (
    <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: [1], minHeight: 0 }}>
        <Box sx={{ ml: -1, ...(!open && { display: 'none' }) }} display="flex" justifyContent="space-between" alignItems="center">
            {/* Company/User info could go here */}
        </Box>

        <IconButton onClick={toggleDrawer} sx={{ ...(!open && { display: 'none' }), fontSize: '1.25rem' }}>
            <IconMainDrawerCloseDrawer />
        </IconButton>

        <IconButton edge="start" aria-label="open drawer" onClick={toggleDrawer} sx={{ ...(open && { display: 'none' }), ml: '.1rem', fontSize: '1.25rem' }}>
            <IconMainDrawerOpenDrawer />
        </IconButton>
    </Toolbar>
);

interface BuildInfoProps {
    open: boolean;
}

const BuildInfo = ({ open }: BuildInfoProps) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pb: 2, ...(!open && { display: 'none' }) }}>
        <Typography variant="navText" sx={{ textAlign: 'center' }}>
            <Typography variant="navText" component="span" sx={{ fontWeight: 450 }}>Build</Typography> 1.0.6zhhs628jf
        </Typography>
    </Box>
);

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        '& .MuiDrawer-paper': {
            backgroundColor: theme.palette.customDrawer.main,
            borderRight: 'none',
            position: 'relative',
            whiteSpace: 'nowrap',
            width: '240px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                [theme.breakpoints.up('sm')]: {
                    width: '54px',
                },
            }),
        },
    }),
);

const Layout: React.FC<LayoutProps> = () => {

    const { loadingSettings } = useSettings();
    const {
        userAccount,
        showReauthModal,
        dismissReauthModal,
        quickReauth
    } = useAuth();
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

    // Initialize drawer state once on component mount
    useEffect(() => {
        const savedState = localStorage.getItem('frlk-drawer-state');
        if (savedState) {
            setDrawerOpen(savedState === "true");
        }
    }, []);

    const toggleDrawer = useCallback(() => {
        setDrawerOpen(prevOpen => {
            const newState = !prevOpen;
            setTimeout(() => {
                localStorage.setItem('frlk-drawer-state', String(newState));
            }, 0);
            return newState;
        });
    }, []);

    // Get user display name for the reauth modal
    const userDisplayName = useMemo(() => {
        if (!userAccount) return 'your account';

        if (userAccount.username) {
            return `${userAccount.username}`;
        }

        return 'your account';
    }, [userAccount]);

    if (loadingSettings) {
        return <Spinner />;
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <Drawer variant="permanent" open={drawerOpen}>
                <DrawerHeader open={drawerOpen} toggleDrawer={toggleDrawer} />

                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                    <Navigation open={drawerOpen} />
                </Box>

                <Box>
                    <BuildInfo open={drawerOpen} />
                </Box>
            </Drawer>

            <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: drawerOpen ? "calc(100% - 240px)" : "calc(100% - 54px)",
                    height: "100vh",
                    transition: (theme) =>
                        theme.transitions.create("width", {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                }}
            >
                <AppBar />

                <Container
                    disableGutters
                    maxWidth={false}
                    component="main"
                    sx={{
                        backgroundColor: (theme) => theme.palette.customBackground.main,
                        flexGrow: 1, overflow: 'auto', borderTopLeftRadius: 8,
                        borderLeft: (theme) => theme.palette.customDrawer.border
                    }}
                >
                    <Outlet />
                </Container>
            </Box>

            <ReauthModal open={showReauthModal} onClose={dismissReauthModal}
                onReauth={quickReauth} userDisplayName={userDisplayName}
            />
        </Box>
    );
};

export default Layout;